/**
 * voiceOutbox — la file d'envoi des enregistrements : la file commune du moteur
 * (`engine/shared/outbox`), à la voix.
 *
 * Un enregistrement fini porte sa durée ; sa copie durable se range dans `voice-outbox` (les
 * documents de l'app en natif, IndexedDB `masteur-voice-outbox` sur le web) ; ses fils d'Ariane et
 * ses erreurs passent par la télémétrie de la voix (`voice.outbox`, `VoiceOutboxError`). Tout le reste
 * — les états, la persistance, la survie à l'app tuée, un envoi à la fois, la garde du compte qui a
 * enregistré (`owner`) — est celui de la file commune, que la copie d'une activité partage.
 */
import {
    createOutbox,
    createOutboxFileStore,
    useOutbox,
    type KeyValueStorage,
    type Outbox,
    type OutboxFile,
    type OutboxFileStore,
    type OutboxItem,
    type OutboxOwner,
    type OutboxSendContext,
    type OutboxState,
} from "../shared/outbox/outbox"
import { voiceTelemetry } from "./voiceTelemetry"

/** Un enregistrement fini, tel que la file le garde. */
export type VoiceFile = OutboxFile & { durationMs: number }

export type VoiceOutboxItem<Target, Result = unknown> = OutboxItem<Target, Result, VoiceFile>
export type VoiceOutboxState<Target, Result = unknown> = OutboxState<Target, Result, VoiceFile>
export type VoiceOutbox<Target, Result = unknown> = Outbox<Target, Result, VoiceFile>
export type VoiceFileStore = OutboxFileStore<VoiceFile>

export type VoiceOutboxOptions<Target, Result> = {
    /** La clé de la file persistée. Une file par usage (la messagerie, l'activité orale…). */
    storageKey: string
    /** L'envoi : rend la réponse, ou rejette. */
    send: (item: VoiceOutboxItem<Target, Result>, context: OutboxSendContext) => Promise<Result>
    storage?: KeyValueStorage
    fileStore?: VoiceFileStore
    newId?: () => string
    /** Le compte connecté : un vocal ne part que sous le compte qui l'a enregistré (voir la file commune). */
    owner?: () => OutboxOwner | null
}

let sequence = 0
const voiceId = () => `voice-${Date.now().toString(36)}-${(++sequence).toString(36)}`

/** Le dossier des copies durables d'un enregistrement : celui d'avant la file commune, gardé. */
const voiceFileStore = (): VoiceFileStore => createOutboxFileStore<VoiceFile>("voice-outbox")

export function createVoiceOutbox<Target, Result = unknown>(
    options: VoiceOutboxOptions<Target, Result>
): VoiceOutbox<Target, Result> {
    return createOutbox<Target, Result, VoiceFile>({
        storageKey: options.storageKey,
        send: options.send,
        storage: options.storage,
        fileStore: options.fileStore ?? voiceFileStore(),
        newId: options.newId ?? voiceId,
        owner: options.owner,
        name: "voice.outbox",
        errorName: "VoiceOutboxError",
        telemetry: voiceTelemetry,
    })
}

/** La file vue d'un écran : son état, relu à chaque changement. */
export function useVoiceOutbox<Target, Result>(outbox: VoiceOutbox<Target, Result>): VoiceOutboxState<Target, Result> {
    return useOutbox(outbox)
}
